import { htmlParser } from '@/services/htmlParser';
import { Artifact, ArtifactSearchResult, CreateArtifactData, UpdateArtifactData } from '@/types/artifact';
import { StorageError, StorageErrorCode, StorageInfo, StorageService } from '@/types/storage';
import Dexie, { Table } from 'dexie';
import Fuse from 'fuse.js';
import { v4 as uuidv4 } from 'uuid';

class ArtifactDatabase extends Dexie {
  artifacts!: Table<Artifact>;

  constructor() {
    super('ArtifactSandbox');

    this.version(1).stores({
      artifacts: 'id, title, createdAt, updatedAt'
    });

    this.version(2).stores({
      artifacts: 'id, title, createdAt, updatedAt'
    });
  }
}

export class IndexedDBService implements StorageService {
  private db: ArtifactDatabase;
  private fuse: Fuse<Artifact> | null = null;

  constructor() {
    this.db = new ArtifactDatabase();
  }

  async createArtifact(data: CreateArtifactData): Promise<Artifact> {
    try {
      const now = new Date();
      const metadata = htmlParser.extractMetadata(data.htmlContent);

      const artifact: Artifact = {
        id: uuidv4(),
        title: data.title.trim() || 'Untitled Artifact',
        htmlContent: data.htmlContent,
        createdAt: now,
        updatedAt: now,
        metadata,
      };

      await this.db.artifacts.add(artifact);
      await this.updateSearchIndex();

      return artifact;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async getArtifact(id: string): Promise<Artifact | null> {
    try {
      const artifact = await this.db.artifacts.get(id);
      return artifact || null;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async getAllArtifacts(): Promise<Artifact[]> {
    try {
      const artifacts = await this.db.artifacts.orderBy('updatedAt').reverse().toArray();
      // This is expensive, should be called only when data changes significantly
      // await this.updateSearchIndex(); 
      return artifacts;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async updateArtifact(id: string, updates: UpdateArtifactData): Promise<Artifact> {
    try {
      const existingArtifact = await this.db.artifacts.get(id);
      if (!existingArtifact) {
        throw new StorageError(StorageErrorCode.NOT_FOUND, 'Artifact not found');
      }

      const updatedData: Partial<Artifact> = {
        ...updates,
        updatedAt: new Date(),
      };

      if (updates.htmlContent) {
        updatedData.metadata = htmlParser.extractMetadata(updates.htmlContent);
      }

      await this.db.artifacts.update(id, updatedData);

      const updatedArtifact: Artifact = { ...existingArtifact, ...updatedData };

      await this.updateSearchIndex();
      return updatedArtifact;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async deleteArtifact(id: string): Promise<boolean> {
    try {
      await this.db.artifacts.delete(id);
      await this.updateSearchIndex();
      return true;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async importArtifacts(artifacts: Artifact[]): Promise<void> {
    try {
      await this.db.artifacts.bulkAdd(artifacts);
      await this.updateSearchIndex();
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async exportArtifacts(): Promise<Artifact[]> {
    try {
      return await this.db.artifacts.toArray();
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async searchArtifacts(query: string): Promise<ArtifactSearchResult[]> {
    try {
      if (!query.trim()) {
        const artifacts = await this.getAllArtifacts();
        return artifacts.map(artifact => ({
          artifact,
          score: 1,
          matches: [],
        }));
      }

      if (!this.fuse) {
        await this.updateSearchIndex();
      }

      const results = this.fuse!.search(query);
      return results.map(result => ({
        artifact: result.item,
        score: result.score || 0,
        matches: result.matches?.map(match => match.key || '') || [],
      }));
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }


  async clearAll(): Promise<void> {
    try {
      await this.db.artifacts.clear();
      this.fuse = null;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  async getStorageInfo(): Promise<StorageInfo> {
    try {
      const artifacts = await this.db.artifacts.toArray();
      const totalSize = artifacts.reduce((sum, artifact) =>
        sum + artifact.metadata.size, 0
      );

      let storageQuota = 0;
      let storageUsed = 0;

      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        storageQuota = estimate.quota || 0;
        storageUsed = estimate.usage || 0;
      }

      return {
        totalArtifacts: artifacts.length,
        totalSize,
        storageQuota,
        storageUsed,
        availableSpace: storageQuota - storageUsed,
      };
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  private async updateSearchIndex(): Promise<void> {
    const artifacts = await this.db.artifacts.toArray();

    this.fuse = new Fuse(artifacts, {
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'tags', weight: 0.5 },
        { name: 'htmlContent', weight: 0.3 },
      ],
      threshold: 0.4,
      includeScore: true,
      includeMatches: true,
    });
  }

  private handleStorageError(error: unknown): StorageError {
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        return new StorageError(
          StorageErrorCode.QUOTA_EXCEEDED,
          'Storage quota exceeded'
        );
      }
    }

    if (error instanceof StorageError) {
      return error;
    }

    return new StorageError(
      StorageErrorCode.STORAGE_UNAVAILABLE,
      'Storage operation failed',
      { originalError: error }
    );
  }
} 